import React, { useState } from 'react';
import { populationEntries } from '../../data/populations';
import { useScenario } from '../../contexts/ScenarioContext';
import { getSourceById } from '../../utils/sourceIndex';
import { PopulationEntry, Tier } from '../../types/census';
import { SourceRef } from '../SourceRef';
const MIN = 1e4;
const MAX = 3e10;
const LMIN = Math.log10(MIN);
const LMAX = Math.log10(MAX);
const pos = (v: number) => (Math.log10(Math.min(MAX, Math.max(MIN, v))) - LMIN) / (LMAX - LMIN) * 100;
const TICKS: Array<[number, string]> = [[1e4, '10k'], [1e5, '100k'], [1e6, '1M'], [1e7, '10M'], [1e8, '100M'], [1e9, '1B'], [1e10, '10B']];
const TIER_LABEL: Record<Tier, string> = {
  device: 'Device-embedded assistants',
  created: 'Created agents',
  ambient: 'Ambient devices'
};
const TIER_ORDER: Tier[] = ['device', 'created', 'ambient'];
export function PopulationBreakdown() {
  const {
    scenario
  } = useScenario();
  const [activeId, setActiveId] = useState(populationEntries[0].id);
  const active = populationEntries.find(e => e.id === activeId) ?? populationEntries[0];
  return <section aria-labelledby="breakdown-title" className="mx-auto max-w-[1400px] px-5 pt-14 md:px-8" data-id="element-42" data-mp="3gvb6pl2gflie">
      <div className="max-w-2xl" data-id="element-43" data-mp="3lm1grbltscjf">
        <h2 id="breakdown-title" className="font-serif text-3xl text-ink md:text-4xl" data-id="element-44" data-mp="3ln30e4gwq7cr">
          Who the machines are
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-45" data-mp="3qngyouspi2xl">
          Each bar spans the low–high band on a log scale; the notch marks our base count. Populations outside the current scenario are dimmed.
        </p>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-46" data-mp="32vtpprvehcjl">
        <div data-id="element-47" data-mp="36xf0n7odjgr5">
          <div className="relative ml-0 hidden h-5 md:ml-[16rem] md:mr-[6.5rem] md:block" aria-hidden="true" data-id="element-48" data-mp="36y6mhqs66048">
            {TICKS.map(([v, l]) => <span key={l} className="absolute -translate-x-1/2 font-mono text-[10.5px] text-muted" style={{
            left: `${pos(v)}%`
          }} data-id="element-49" data-mp="i7x9q7z2j6oc">
                {l}
              </span>)}
          </div>
          {TIER_ORDER.map(tier => {
          const rows = populationEntries.filter(e => e.tier === tier);
          const included = scenario.tiers.includes(tier);
          return <div key={tier} className="mt-6 first:mt-2" data-id="element-50" data-mp="2s6nwsdmx34zr">
                <h3 className="flex items-baseline gap-3 border-b border-line pb-2 text-[13px] font-medium text-ink" data-id="element-51" data-mp="2naqgg7bnlymz">
                  {TIER_LABEL[tier]}
                  {!included && <span className="font-mono text-[10.5px] font-normal text-muted" data-id="element-52" data-mp="1virff6jno5nh">outside current scenario</span>}
                </h3>
                <ul data-id="element-53" data-mp="29ao2gw6gvpd0">
                  {rows.map(e => <BarRow key={e.id} entry={e} dimmed={!included} active={e.id === activeId} onActivate={() => setActiveId(e.id)} data-id="element-54" data-mp="24jftg191u60d" />)}
                </ul>
              </div>;
        })}
        </div>

        <aside aria-live="polite" className="lg:sticky lg:top-28 lg:self-start" data-id="element-55" data-mp="1bth0hurqhoqf">
          <Readout entry={active} data-id="element-56" data-mp="3nhc4lok2x0us" />
        </aside>
      </div>
    </section>;
}
interface BarRowProps {
  entry: PopulationEntry;
  dimmed: boolean;
  active: boolean;
  onActivate: () => void;
}
function BarRow({
  entry,
  dimmed,
  active,
  onActivate
}: BarRowProps) {
  const lo = pos(entry.low);
  const hi = pos(entry.high);
  return <li className={`grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 border-b border-line/60 py-2.5 md:grid-cols-[15rem_minmax(0,1fr)_5.5rem] ${dimmed ? 'opacity-40' : ''}`} onMouseEnter={onActivate} data-id="element-57" data-mp="3jgs7um3f3z96">
      <button type="button" onFocus={onActivate} onClick={onActivate} aria-pressed={active} className={`truncate text-left text-[13.5px] transition-colors duration-150 ${active ? 'text-machine' : 'text-ink/90 hover:text-ink'}`} data-id="element-58" data-mp="ezpnab1tfzdn">
        {entry.name}
      </button>
      <div className="relative col-span-2 h-4 md:col-span-1 md:col-start-2 md:row-start-1" aria-hidden="true" data-id="element-59" data-mp="2qrl4n9t0qlhl">
        <div className="absolute inset-y-[7px] left-0 right-0 bg-line/50" data-id="element-60" data-mp="361rmqobticmw" />
        <div className={`absolute inset-y-[3px] rounded-sm ${active ? 'bg-machine/45' : 'bg-machine/25'}`} style={{
        left: `${lo}%`,
        width: `${Math.max(0.5, hi - lo)}%`
      }} data-id="element-61" data-mp="3632nef9yv49s" />
        <div className={`absolute inset-y-0 w-[3px] -translate-x-1/2 rounded-sm ${active ? 'bg-ink' : 'bg-machine'}`} style={{
        left: `${pos(entry.base)}%`
      }} data-id="element-62" data-mp="2s9tkmxgixh08" />
      </div>
      <p className="col-start-2 row-start-1 text-right font-mono text-[13px] tabular text-ink md:col-start-3" data-id="element-63" data-mp="3ozayhzpnu9d0">
        {entry.display}
        <SourceRef id={entry.sourceId} estimate={entry.isEstimate && entry.rangeDisplay ? {
        low: entry.rangeDisplay.split('–')[0],
        high: entry.rangeDisplay.split('–')[1] ?? entry.display
      } : undefined} note={entry.note} data-id="element-64" data-mp="3fzfldpcjo4ac" />
      </p>
    </li>;
}
function Readout({
  entry
}: {
  entry: PopulationEntry;
}) {
  const src = getSourceById(entry.sourceId);
  return <div className="rounded-lg border border-line bg-surface p-5" data-id="element-65" data-mp="1zjuq4mjk8ro6">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-muted" data-id="element-66" data-mp="1piaa5n1ho9bg">{TIER_LABEL[entry.tier]}</p>
      <h3 className="mt-2 text-lg leading-snug text-ink" data-id="element-67" data-mp="1uh3otisii6d7">{entry.name}</h3>
      <p className="mt-4 font-mono text-4xl tabular text-machine" data-id="element-68" data-mp="1zevj2jgmq4kk">{entry.display}</p>
      <p className="mt-1 font-mono text-[12px] text-muted" data-id="element-69" data-mp="245vdni2a0ajw">
        {entry.rangeDisplay ? `Range ${entry.rangeDisplay}` : 'Single dated measurement'}
        {entry.isEstimate && <span className="ml-2 rounded border border-machine/40 px-1.5 py-px text-[10.5px] text-machine" data-id="element-70" data-mp="289tblpfv7fbd">estimate</span>}
      </p>
      <p className="mt-4 text-[13.5px] leading-relaxed text-ink/85" data-id="element-71" data-mp="2i6662pj8hx76">{entry.basis}</p>
      {entry.note && <p className="mt-2 text-[13px] text-muted" data-id="element-72" data-mp="2n4puqxp7pn38">{entry.note}</p>}
      {src && <div className="mt-5 border-t border-line pt-4" data-id="element-73" data-mp="nrvg2dqwkwzt">
          <p className="font-mono text-[10.5px] uppercase tracking-wider text-muted" data-id="element-74" data-mp="ntg91yxkk7vk">Source [{src.n}]</p>
          {src.url.startsWith('/') ? <p className="mt-1 text-[13px] text-ink" data-id="element-75" data-mp="kqad9i3nx95c">{src.name}</p> : <a href={src.url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-words text-[13px] text-ink underline decoration-line underline-offset-4 hover:decoration-machine" data-id="element-76" data-mp="ks3ozbt4ujk7">
              {src.name}
            </a>}
          <p className="mt-1 break-all font-mono text-[11px] text-muted" data-id="element-77" data-mp="pobxw7gqlgix">{src.url}</p>
        </div>}
    </div>;
}